import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from './supabase';
import { useI18n } from './i18n';
import { authErrorParams, authErrorType } from './authErrors';
import { trackAuthEvent } from './authAttribution';

const messages = {
  en: { title: 'Confirm your email', body: 'Open the confirmation link in your inbox to finish creating your account. Your next step is saved.', resend: 'Resend confirmation email', wait: 'Resend available in {seconds}s', sent: 'Confirmation requested. Check your inbox and spam folder.', failed: 'The confirmation could not be sent. Check the address and try again.', rate: 'Please wait before requesting another email.', signin: 'Already confirmed? Sign in', edit: 'Use a different email address' },
  de: { title: 'Bestätige deine E-Mail', body: 'Öffne den Bestätigungslink in deinem Postfach, um dein Konto fertigzustellen. Dein nächster Schritt bleibt gespeichert.', resend: 'Bestätigungs-E-Mail erneut senden', wait: 'Erneut senden in {seconds} s', sent: 'Bestätigung angefordert. Prüfe dein Postfach und den Spam-Ordner.', failed: 'Die Bestätigung konnte nicht gesendet werden. Prüfe die Adresse und versuche es erneut.', rate: 'Warte bitte, bevor du eine weitere E-Mail anforderst.', signin: 'Bereits bestätigt? Anmelden', edit: 'Andere E-Mail-Adresse verwenden' },
  fr: { title: 'Confirmez votre email', body: 'Ouvrez le lien reçu par email pour terminer votre compte. Votre prochaine étape reste enregistrée.', resend: 'Renvoyer l’email de confirmation', wait: 'Renvoi possible dans {seconds} s', sent: 'Confirmation demandée. Consultez votre boîte de réception et les courriers indésirables.', failed: 'Impossible d’envoyer la confirmation. Vérifiez l’adresse et réessayez.', rate: 'Veuillez patienter avant de demander un nouvel email.', signin: 'Déjà confirmé ? Se connecter', edit: 'Utiliser une autre adresse email' },
};

export default function EmailConfirmationPanel({ email, redirectUrl, redirectPath, onEdit, initialCooldown = 60 }) {
  const { language } = useI18n();
  const copy = messages[language] || messages.en;
  const [waitUntil, setWaitUntil] = useState(() => Date.now() + initialCooldown * 1000);
  const [clock, setClock] = useState(Date.now);
  const remaining = Math.max(0, Math.ceil((waitUntil - clock) / 1000));
  const coolingDown = remaining > 0;
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('');
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (!coolingDown) return undefined;
    const timer = setInterval(() => setClock(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [coolingDown]);
  async function resend() {
    if (loading || remaining) return;
    setLoading(true); setFailed(false); setStatus('');
    try {
      const { error } = await supabase.auth.resend({ type: 'signup', email,
        options: { emailRedirectTo: redirectUrl } });
      if (error) throw error;
      setClock(Date.now()); setWaitUntil(Date.now() + 60000); setStatus(copy.sent);
      trackAuthEvent('signup_confirmation_resent', { method: 'email' });
    } catch (error) {
      setFailed(true); setClock(Date.now()); setWaitUntil(Date.now() + 60000);
      setStatus(authErrorType(error) === 'rate_limit' ? copy.rate : copy.failed);
      trackAuthEvent('signup_confirmation_resend_error', { method: 'email', ...authErrorParams(error, 'confirmation_resend') });
    } finally { setLoading(false); }
  }
  return <section className="email-confirmation-panel" aria-label={copy.title}>
    <h2>{copy.title}</h2><p>{copy.body}</p><p className="email-confirmation-address">{email}</p>
    <button type="button" className="auth-btn primary-btn" disabled={loading || remaining > 0} onClick={resend}>{remaining ? copy.wait.replace('{seconds}', remaining) : copy.resend}</button>
    {status && <p role={failed ? 'alert' : 'status'}>{status}</p>}
    <p><Link to="/login" state={{ email, showEmailLogin: true, from: redirectPath }}>{copy.signin}</Link></p>
    {onEdit && <button type="button" className="auth-btn secondary-btn" onClick={onEdit}>{copy.edit}</button>}
  </section>;
}
